import { Col, Row, Skeleton } from 'antd'

import { ReactComponent as RemoveIcon } from 'features/Cart/assets/trash.svg'
import { useMemo } from 'react'
import type { PromotionSlotRoom } from 'features/Home/Promotion/types/promotion.types'
import { useFetchLeaseSlotDetailsQuery } from 'features/Home/Book/state/api/bookApi'
import { Show } from 'common/components/Show/Show'
import { INFO_CONSTANTS } from 'features/Home/constants/infoConstants'
import { getRoomName } from 'features/Home/Promotion/PromotionDetails/Rooms/Rooms'
import { isSlotStartInPast } from './slotTime'

type RoomDetailsType = {
  id: number
  name: string
  timeSlot: string
  date: string
  roomAliasId?: number
  status?: string
}

type Props = {
  room: RoomDetailsType
  isDeleteDisabled?: boolean
  hasError?: boolean
  handleDeleteBooking: () => void
  roomsToDelete: PromotionSlotRoom[]
  isGrouped?: boolean
  bookingId?: string
  siteId?: string
  slotId?: number
  slotStartTime?: string
  timeZone?: string
}

const hasSlotDetailValue = (value?: string | null) => Boolean(value?.trim())

const SlotDetailRow = ({
  label,
  value,
  isLoading,
}: {
  label: string
  value?: string
  isLoading?: boolean
}) => (
  <div className='slot-detail-row'>
    <span className='slot-detail-label'>{label}</span>
    <Show
      when={!isLoading}
      fallback={<Skeleton paragraph={{ rows: 0 }} active className='slot-detail-skeleton' />}>
      <span className='slot-detail-value'>{value}</span>
    </Show>
  </div>
)

const SlotDetails = ({
  room,
  isDeleteDisabled = false,
  hasError = false,
  handleDeleteBooking,
  roomsToDelete,
  isGrouped,
  bookingId,
  siteId,
  slotId,
  slotStartTime,
  timeZone,
}: Props) => {
  const { data: slotDetails, isFetching: isFetchingSlotDetails } = useFetchLeaseSlotDetailsQuery(
    {
      siteId: siteId!,
      bookingId: bookingId!,
      date: room.date,
      slotId: slotId!,
      roomAliasId: room.roomAliasId!,
    },
    {
      skip: !bookingId || !siteId || !slotId || !room.roomAliasId,
    },
  )

  const showAssignedRoom =
    isFetchingSlotDetails || hasSlotDetailValue(slotDetails?.room)
  const showOptions = isFetchingSlotDetails || hasSlotDetailValue(slotDetails?.options)
  const showDetailRows = showAssignedRoom || showOptions

  const isInPast = useMemo(() => {
    if (!roomsToDelete || roomsToDelete.length === 0) {
      return false
    }

    const groupStartTime = roomsToDelete
      .map((r) => r.start_time)
      .filter(Boolean)
      .sort()[0]

    const startTime = isGrouped ? groupStartTime : slotStartTime
    return isSlotStartInPast({ date: room.date, startTime, timeZone })
  }, [isGrouped, roomsToDelete, room.date, slotStartTime, timeZone])

  return (
    <Row className='slot-container' data-error={hasError}>
      <Col xs={24}>
        <Row justify='space-between' align='middle'>
          <Col>
            <span className='room-label'>{getRoomName(room.name, true)}</span>
          </Col>
          {!isInPast && (
            <Col>
              <button
                disabled={isDeleteDisabled}
                className='remove-button'
                onClick={handleDeleteBooking}>
                <RemoveIcon />
              </button>
            </Col>
          )}
        </Row>
        <Row>
          <span className='room-date'>{room.timeSlot}</span>
        </Row>
        {showDetailRows && (
          <div className='slot-detail-rows'>
            {showAssignedRoom && (
              <SlotDetailRow
                label={INFO_CONSTANTS.ASSIGNED_ROOM}
                value={slotDetails?.room}
                isLoading={isFetchingSlotDetails}
              />
            )}
            {showOptions && (
              <SlotDetailRow
                label={INFO_CONSTANTS.OPTIONS}
                value={slotDetails?.options}
                isLoading={isFetchingSlotDetails}
              />
            )}
          </div>
        )}
      </Col>
    </Row>
  )
}

export default SlotDetails

export const SlotDetailsList = ({
  rooms,
  isDeleteDisabled = false,
  handleDeleteBooking,
  roomsToDelete,
  isGrouped,
  bookingId,
  siteId,
  slotId,
  slotStartTime,
  timeZone,
}: {
  rooms: RoomDetailsType[]
  isDeleteDisabled?: boolean
  handleDeleteBooking: (hasRoomError: boolean, bookingId: number) => void
  roomsToDelete: PromotionSlotRoom[]
  isGrouped?: boolean
  bookingId?: string
  siteId?: string
  slotId?: number
  slotStartTime?: string
  timeZone?: string
}) => {
  return (
    <div className='slot-details-list'>
      {rooms.map((room) => {
        const hasRoomError = room?.status === 'error'
        return (
          <SlotDetails
            isDeleteDisabled={isDeleteDisabled}
            roomsToDelete={roomsToDelete}
            key={room.id}
            room={room}
            isGrouped={isGrouped}
            hasError={hasRoomError}
            bookingId={bookingId}
            siteId={siteId}
            slotId={slotId}
            slotStartTime={slotStartTime}
            timeZone={timeZone}
            handleDeleteBooking={() => handleDeleteBooking(hasRoomError, room.id)}
          />
        )
      })}
    </div>
  )
}
